import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { FiX, FiSearch, FiUser, FiCheck, FiUserX } from 'react-icons/fi';

/**
 * ReassignModal — shop-floor bottom sheet that lets a team lead hand a task or a
 * job card to a different staff member. Presentational + self-fetching: it loads
 * the garage's available mechanics (workload-sorted) from
 * GET /api/users/mechanics/available and calls `onSelect(mechanicId)` with the
 * chosen user's id (or null when "Unassign" is tapped). The CALLER owns the
 * actual reassign request, because the endpoint differs by target:
 *   - task:     PATCH /api/tasks/:taskId/assign        { assigned_mechanic_id }
 *   - job card: PATCH /api/job-cards/:id/assign        { mechanic_id }
 *
 * Big touch targets, search, RTL-aware. Gated by the caller on the relevant
 * permission (tasks.assign / job_cards.assign) so it never renders for a plain
 * staff member.
 *
 * Props:
 *   isOpen, onClose
 *   title              — localized heading
 *   currentAssigneeId  — highlighted as "Currently"
 *   garageId           — the branch the work lives in; scopes the candidate list
 *                        to who may actually be assigned there. Omitted, the
 *                        endpoint falls back to its old caller-scoped answer.
 *   allowUnassign      — show the Unassign row (default true)
 *   onSelect(id|null)  — async; caller performs the API call and closes on success
 */
const ReassignModal = ({
  isOpen,
  onClose,
  title,
  currentAssigneeId = null,
  garageId = null,
  allowUnassign = true,
  onSelect,
}) => {
  const { t } = useTranslation();
  const [mechanics, setMechanics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(undefined); // id being submitted ('' = unassign)

  useEffect(() => {
    if (!isOpen) return;
    let alive = true;
    setLoading(true);
    setSearch('');
    // Scoped to the branch this work belongs to. Company-wide, this sheet
    // offered a team lead mechanics from OTHER branches, whom the assignee
    // validator then refused — the picker/validator mismatch this change closes.
    axios
      .get('/api/users/mechanics/available', {
        params: garageId ? { branch: garageId } : undefined,
      })
      .then((res) => {
        if (alive) setMechanics(res.data?.data || []);
      })
      .catch(() => {
        if (alive) setMechanics([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [isOpen, garageId]);

  const fullName = (m) => `${m.first_name || ''} ${m.last_name || ''}`.trim() || m.email || '—';

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return mechanics;
    return mechanics.filter(
      (m) => fullName(m).toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q)
    );
  }, [mechanics, search]);

  if (!isOpen) return null;

  const handlePick = async (id) => {
    try {
      setBusyId(id === null ? '' : id);
      await onSelect(id);
    } finally {
      setBusyId(undefined);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center bg-black/50"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full sm:max-w-md bg-surface rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-edge">
          <h2 className="text-lg font-bold text-content">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('close')}
            className="w-11 h-11 inline-flex items-center justify-center rounded-xl text-content-muted hover:bg-surface-2"
          >
            <FiX size={22} />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 pb-2">
          <div className="relative">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('staff.reassign.search')}
              className="w-full px-4 py-3 ps-11 rounded-xl bg-surface-2 text-content placeholder:text-content-muted focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <FiSearch className="absolute start-4 top-1/2 -translate-y-1/2 text-content-muted" size={18} />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2">
          {loading ? (
            <div className="py-10 text-center text-content-muted">…</div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-content-muted">{t('staff.reassign.noStaff')}</div>
          ) : (
            <>
              {allowUnassign && (() => {
                const isCurrentlyUnassigned = currentAssigneeId == null;
                return (
                  <button
                    type="button"
                    onClick={() => handlePick(null)}
                    disabled={busyId !== undefined || isCurrentlyUnassigned}
                    aria-current={isCurrentlyUnassigned}
                    className={`w-full min-h-[56px] flex items-center gap-3 p-3 rounded-2xl border transition-colors ${
                      isCurrentlyUnassigned
                        ? 'border-accent bg-accent/10'
                        : 'border-edge bg-surface hover:bg-surface-2'
                    } disabled:opacity-60`}
                  >
                    <span className="w-10 h-10 rounded-full bg-surface-3 inline-flex items-center justify-center text-content-muted shrink-0">
                      <FiUserX size={20} />
                    </span>
                    <span className="flex-1 text-start font-semibold text-content">
                      {t('staff.reassign.unassign')}
                    </span>
                    {isCurrentlyUnassigned ? (
                      <span className="text-xs font-semibold text-accent shrink-0">
                        {t('staff.reassign.currentLabel')}
                      </span>
                    ) : busyId === '' ? (
                      <Spinner />
                    ) : null}
                  </button>
                );
              })()}

              {filtered.map((m) => {
                const isCurrent = m.id === currentAssigneeId;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => handlePick(m.id)}
                    disabled={busyId !== undefined || isCurrent}
                    aria-current={isCurrent}
                    className={`w-full min-h-[56px] flex items-center gap-3 p-3 rounded-2xl border transition-colors text-start ${
                      isCurrent
                        ? 'border-accent bg-accent/10'
                        : 'border-edge bg-surface hover:bg-surface-2'
                    } disabled:opacity-60`}
                  >
                    <span className="w-10 h-10 rounded-full bg-accent/15 text-accent inline-flex items-center justify-center font-bold shrink-0">
                      {(m.first_name || '?').charAt(0).toUpperCase()}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block font-semibold text-content truncate">{fullName(m)}</span>
                      {typeof m.activeJobsCount === 'number' && (
                        <span className="block text-xs text-content-muted">
                          {m.activeJobsCount} <FiUser className="inline -mt-0.5" size={11} />
                        </span>
                      )}
                    </span>
                    {isCurrent ? (
                      <span className="text-xs font-semibold text-accent shrink-0">
                        {t('staff.reassign.currentLabel')}
                      </span>
                    ) : busyId === m.id ? (
                      <Spinner />
                    ) : (
                      <FiCheck className="text-content-muted shrink-0" size={20} />
                    )}
                  </button>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Spinner = () => (
  <div className="animate-spin rounded-full h-5 w-5 border-2 border-accent border-t-transparent shrink-0" />
);

export default ReassignModal;
